import { keyBy } from "lodash";
import groupBy from "lodash/groupBy";
import intersection from "lodash/intersection";
import partition from "lodash/partition";
import { all, put, select, takeEvery } from "redux-saga/effects";
import { v4 } from "uuid";

import {
  MasterRuleAction,
  MasterRuleBookAction,
  RuleAction,
  RuleBookAction,
} from "actions";

import { RuleSetAction } from "actions/ruleSets";

import {
  MASTER_RULE,
  MASTER_RULE_BOOK,
  RULE,
  RULE_BOOK,
} from "constants/actionTypes";

import {
  SynchronisedRuleSetFields,
  isManagedRuleOutOfSync,
  isManagedRuleSetOutOfSync,
  SynchronisedRuleFields,
} from "lib/rules";

import {
  getLastOrderByMasterRuleBookId,
  getLastOrderByRuleBookId,
  getMasterRuleBookById,
  getMasterRuleById,
  getMasterRuleIdsByMasterRuleBookId,
  getMasterRules,
  getMasterRulesByMasterRuleBookId,
  getMasterRuleSets,
  getRuleBookById,
  getRuleById,
  getRuleIdsByMasterRuleId,
  getRuleIdsByRuleBookId,
  getRules,
  getRulesByRuleBookId,
  selectRuleIdsByMasterRuleIdLookup,
  getRuleSetIdsByMasterRuleSetId,
  getRuleSetIdsByRuleBookId,
  getRuleSetById,
  selectMasterRulesByMasterRuleSetIdLookup,
  getMasterRuleSetIdsByMasterRuleBookId,
} from "selectors";

import {
  getMasterRuleSetById,
  getMasterRuleSetsByMasterRuleBookId,
  getRuleSetsByRuleBookId,
} from "selectors/ruleSets";
import isEqual from "lodash/isEqual";

function provisionManagedRuleSetPayload(
  masterRuleSetId: string,
  ruleBookId: string,
  state: RootState,
) {
  const ruleSetId = v4();
  const masterRuleSet: MasterRuleSet =
    getMasterRuleSetById(masterRuleSetId)(state);

  const ruleSet: RuleSet = {
    id: ruleSetId,
    master_rule_set_id: masterRuleSetId,
    rule_book_id: ruleBookId,
    name: `Managed Rule Set for ${masterRuleSet.name}`,
    input_field: masterRuleSet.input_field,
    criteria: [...masterRuleSet.criteria],
  };

  return ruleSet;
}

/*
Subscribing to Master Rules - How it works:

A Managed Rule Set for each Master Rule Set in the given Master Rule Book is created.
When the Master Rule Set already exists in the Rule Book, it is updated to match the Master Rule Set.

A Managed Rule for each Master Rule in the given Master Rule Book is created.
When the Master Rule already exists in the Rule Book, it is updated to match the Master Rule.

Managing Prerequisite Rules:
A Master Rule can specify a Prerequisite Rules from 0 or more other Master Rule Books.
The Managed Rule will attempt to find an existing Managed Rule in the Rule Book that matches the Master Rule's Prerequisite Rule.
If it finds one, it will use the ID of that Managed Rule as one of its prerequisite rule.



Things to consider:
- When someone unsubscribes from a Master Rule Book, which is providing a Prerequisite Rule to another Managed Rule, that Managed Rule may no longer have any prerequisite rule.
- When someone subscribes to a Master Rule Book, we need to check if there are any existing Managed Rules in the Rule Book were one of the Master Rule's Prerequisites is in the new Master Rule Book
- In which case, we should subscribe the existing Managed Rule to the new Managed Rule.

 */
function* categoriseMasterRuleSets(masterRuleSets) {
  const masterRulesByMasterRuleSetIdLookup: Record<string, MasterRule[]> =
    yield select(
      selectMasterRulesByMasterRuleSetIdLookup as (
        state,
      ) => Record<string, MasterRule[]>,
    );

  const activeMasterRuleSets = [];
  const deprecatedMasterRuleSets = [];
  masterRuleSets.forEach(masterRuleSet => {
    const rules = masterRulesByMasterRuleSetIdLookup[masterRuleSet.id] || [];
    if (rules.some(rule => !rule.is_deleted)) {
      activeMasterRuleSets.push(masterRuleSet);
    } else {
      deprecatedMasterRuleSets.push(masterRuleSet);
    }
  });

  return { activeMasterRuleSets, deprecatedMasterRuleSets };
}

interface DryRunModelResults<Type> {
  // A List of items that are missing in the target Rule Book
  missing: Array<Type>;

  // A List of items that are in the target Rule Book, but not in the source Rule Book
  deprecated: Array<Type>;

  // A List of items that are in both Rule Books, but have different values
  outOfSync: Array<Type>;

  // A List of items that are in both Rule Books, and have the same content
  inSync: Array<Type>;
}

interface RuleSetDryRunModelResults extends DryRunModelResults<RuleSet> {
  masterRuleSetIdToManagedRuleSetId: { [masterRuleSetId: string]: string };
}

interface RuleDryRunModelResults
  extends DryRunModelResults<Rule | RuleEditableFields> {
  masterRuleIdToManagedRuleId: { [masterRuleId: string]: string };
}

interface SyncDryRunResults {
  ruleSets: RuleSetDryRunModelResults;
  rules: RuleDryRunModelResults;
}

function* syncManagedRuleSetsDryRun(
  masterRuleSets: Array<MasterRuleSet>,
  ruleBookId: string,
) {
  const results: RuleSetDryRunModelResults = {
    deprecated: [],
    inSync: [],
    missing: [],
    outOfSync: [],
    masterRuleSetIdToManagedRuleSetId: {},
  };

  const state = yield select();

  // Filter the RuleSets to only active ones
  const { activeMasterRuleSets, deprecatedMasterRuleSets } =
    yield categoriseMasterRuleSets(masterRuleSets);

  activeMasterRuleSets.forEach(masterRuleSet => {
    const managedRuleSetIds: string[] = intersection(
      getRuleSetIdsByMasterRuleSetId(masterRuleSet.id)(state),
      getRuleSetIdsByRuleBookId(ruleBookId)(state),
    );

    const managedRuleSetId =
      managedRuleSetIds.length > 0 ? managedRuleSetIds[0] : null;

    if (managedRuleSetId) {
      results.masterRuleSetIdToManagedRuleSetId[masterRuleSet.id] =
        managedRuleSetId;
      const managedRuleSet: RuleSet = getRuleSetById(managedRuleSetId)(state);

      if (isManagedRuleSetOutOfSync(managedRuleSet, masterRuleSet)) {
        results.outOfSync.push(
          SynchronisedRuleSetFields.reduce<RuleSet>(
            (acc, field: string) => {
              acc[field] = masterRuleSet[field];
              return acc;
            },
            { ...managedRuleSet },
          ),
        );
      } else {
        results.inSync.push(managedRuleSet);
      }
    }

    const ruleSet = provisionManagedRuleSetPayload(
      masterRuleSet.id,
      ruleBookId,
      state,
    );
    results.missing.push(ruleSet);
    results.masterRuleSetIdToManagedRuleSetId[masterRuleSet.id] = ruleSet.id;
  });

  // Insert the deprecated Master Rule Sets to the deprecated array
  deprecatedMasterRuleSets
    .map(masterRuleSet =>
      intersection(
        getRuleSetIdsByMasterRuleSetId(masterRuleSet.id)(state),
        getRuleSetIdsByRuleBookId(ruleBookId)(state),
      ),
    )
    .filter(managedRuleSetIds => managedRuleSetIds.length === 1)
    .map(managedRuleSetId => getRuleSetById(managedRuleSetId)(state))
    .forEach(managedRuleSet => results.deprecated.push(managedRuleSet));

  return results;
}

function* syncManagedRulesDryRun(
  ruleBookId: string,
  masterRules: MasterRule[],
  managedRuleSetLookup: { [masterRuleSetId: string]: string },
) {
  // For each Master Rule in the Master Rule Book, create or update (where required) a Managed Rule in the Rule Book
  const result: RuleDryRunModelResults = {
    deprecated: [],
    inSync: [],
    missing: [],
    outOfSync: [],
    masterRuleIdToManagedRuleId: {},
  };

  const state = yield select();

  const lastOrder = getLastOrderByRuleBookId(ruleBookId)(state);

  masterRules.forEach(masterRule => {
    const managedRuleIds: string[] = intersection(
      // Add The Rules that are linked to the Master Rule
      getRuleIdsByMasterRuleId(masterRule.id)(state),
      // Add The Rules that are linked to the Rule Book
      getRuleIdsByRuleBookId(ruleBookId)(state),
    );

    // managedRuleIds is a list of the intersection of Rule Ids in this Rule Book that are linked to the Master Rule.
    // There will only ever be 1 or 0. Grab out Id of the Managed Rule if there are any.
    const managedRuleId: string =
      managedRuleIds.length === 1 ? managedRuleIds[0] : null;

    if (managedRuleId) {
      result.masterRuleIdToManagedRuleId[masterRule.id] = managedRuleId;

      const rule: Rule = getRuleById(managedRuleId)(state);

      if (masterRule.is_deleted) {
        result.deprecated.push(rule);
      } else if (
        isManagedRuleOutOfSync(rule, masterRule) ||
        rule.rule_set_id !== managedRuleSetLookup[masterRule.rule_set_id]
      ) {
        const managedRule = SynchronisedRuleFields.reduce<Rule>(
          (acc, field: string) => {
            acc[field] = masterRule[field];
            return acc;
          },
          {
            ...rule,
            rule_set_id: managedRuleSetLookup[masterRule.rule_set_id],
          },
        );
        result.outOfSync.push(managedRule);
      } else {
        result.inSync.push(rule);
      }
    } else if (!masterRule.is_deleted) {
      const ruleId = v4();
      const managedRule: RuleEditableFields = {
        id: ruleId,
        comment: masterRule.comment,
        content: masterRule.content,
        criteria: masterRule.criteria,
        gl_code: masterRule.gl_code,
        input_field: masterRule.input_field,
        invoice_line_item_template: masterRule.invoice_line_item_template,
        master_rule_id: masterRule.id,
        name: masterRule.name,
        order: masterRule.order + lastOrder,
        // To be filled in/resolved separately
        prerequisite_rule_ids: [],
        quantity_output_format: masterRule.quantity_output_format,
        quantity_raw_format: masterRule.quantity_raw_format,
        rule_book_id: ruleBookId,
        rule_set_id: managedRuleSetLookup[masterRule.rule_set_id] || null,
        title_template: masterRule.title_template,
        unit_amount_raw_format: masterRule.unit_amount_raw_format,
        unit_amount_output_format: masterRule.unit_amount_output_format,
      };
      result.missing.push(managedRule);
      result.masterRuleIdToManagedRuleId[masterRule.id] = ruleId;
    }
  });
  return result;
}

function* executeRuleSetDryRun(
  ruleSets: RuleSetDryRunModelResults,
  options = null,
) {
  yield all(
    ruleSets.missing.map(ruleSet =>
      put(RuleSetAction.create(ruleSet, options)),
    ),
  );

  yield all(
    ruleSets.outOfSync.map(ruleSet =>
      put(RuleSetAction.update(ruleSet, options)),
    ),
  );

  yield all(
    ruleSets.deprecated.map(ruleSet =>
      put(RuleSetAction.delete(ruleSet.id, options)),
    ),
  );
}

function* executeRuleDryRun(rules: RuleDryRunModelResults, options = null) {
  yield all(rules.missing.map(rule => put(RuleAction.create(rule, options))));

  yield all(rules.outOfSync.map(rule => put(RuleAction.update(rule, options))));

  yield all(
    rules.deprecated.map(rule => put(RuleAction.delete(rule.id, options))),
  );
}

function* getNewPrerequisiteRules(dryRunResults: SyncDryRunResults) {
  const updatedRules = [];

  const state = yield select();

  const futureStateManagedRules = [
    ...dryRunResults.rules.inSync,
    ...dryRunResults.rules.outOfSync,
    ...dryRunResults.rules.missing,
  ]
    .filter(rule => rule.master_rule_id)
    .map((rule): [RuleEditableFields, string[]] => {
      const masterRule: MasterRule = getMasterRuleById(rule.master_rule_id)(
        state,
      );

      const prerequisiteMasterRuleIds = masterRule.prerequisite_rule_ids || [];

      return [rule, prerequisiteMasterRuleIds];
    })
    .filter(
      ([, prerequisiteMasterRuleIds]) => prerequisiteMasterRuleIds.length,
    );

  futureStateManagedRules.forEach(([rule, prerequisiteMasterRuleIds]) => {
    const updatedManagedPrerequisiteRuleIds = prerequisiteMasterRuleIds
      .map(
        masterRuleId =>
          dryRunResults.rules.masterRuleIdToManagedRuleId[masterRuleId],
      )
      .filter(Boolean);

    if (
      !isEqual(rule.prerequisite_rule_ids, updatedManagedPrerequisiteRuleIds)
    ) {
      // The prerequisite rules have changed, we need to update the Managed Rule
      const updatedRule = {
        id: rule.id,
        prerequisite_rule_ids: updatedManagedPrerequisiteRuleIds,
      };
      updatedRules.push(updatedRule);
    }
  });

  return updatedRules;
}

function* onSubscribeToMasterRuleBook(action) {
  const { ruleBookId, masterRuleBookId, options = null } = action;

  const state = yield select();
  const masterRules =
    getMasterRulesByMasterRuleBookId(masterRuleBookId)(state) || [];
  const masterRuleBook = getMasterRuleBookById(masterRuleBookId)(state);

  // Start with creating or updating (when out of sync) a Managed Rule Set for each Master Rule Set in the Master Rule Book
  const masterRuleSets =
    getMasterRuleSetsByMasterRuleBookId(masterRuleBookId)(state) || [];

  const ruleSetResults = yield syncManagedRuleSetsDryRun(
    masterRuleSets,
    ruleBookId,
  );

  const ruleResults = yield syncManagedRulesDryRun(
    ruleBookId,
    masterRules,
    ruleSetResults.masterRuleSetIdToManagedRuleSetId,
  );

  const dryRunResults: SyncDryRunResults = {
    ruleSets: ruleSetResults,
    rules: ruleResults,
  };

  const executeOptions = {
    changeReason: `Subscribed to "${masterRuleBook.name}"`,
    ...options,
  };

  // No prerequisites to process, we can execute the dry run directly
  yield executeRuleSetDryRun(dryRunResults.ruleSets, executeOptions);
  yield executeRuleDryRun(dryRunResults.rules, executeOptions);

  const rewiredContingentRules = yield getNewPrerequisiteRules(dryRunResults);

  executeOptions.changeReason += ". Updated Prerequisite Rules";

  yield all(
    rewiredContingentRules.map(rule =>
      put(RuleAction.update(rule, executeOptions)),
    ),
  );
}

function* onUnsubscribeFromMasterRuleBook(action) {
  const { ruleBookId, masterRuleBookId, options = null } = action;

  const state = yield select();
  const masterRuleBook = getMasterRuleBookById(masterRuleBookId)(state);
  const masterRuleIds =
    getMasterRuleIdsByMasterRuleBookId(masterRuleBookId)(state) || [];
  const masterRuleSetIds =
    getMasterRuleSetIdsByMasterRuleBookId(masterRuleBookId)(state) || [];

  const rules = getRulesByRuleBookId(ruleBookId)(state) || [];

  // For this Rule Book remove all Managed Rules which are related to the Master Rules in the given Master Rule Book
  yield all(
    rules
      .filter(rule => masterRuleIds.includes(rule.master_rule_id))
      .map(rule =>
        put(
          RuleAction.delete(rule.id, {
            changeReason: `Unsubscribed from "${masterRuleBook.name}"`,
            ...options,
          }),
        ),
      ),
  );

  const ruleSets = getRuleSetsByRuleBookId(ruleBookId)(state) || [];

  // For this Rule Book remove all Managed Rule Sets which are part the provided Master Rule Book
  yield all(
    ruleSets
      .filter(ruleSet => masterRuleSetIds.includes(ruleSet.master_rule_set_id))
      .map(managedRuleSet =>
        put(
          RuleSetAction.delete(managedRuleSet.id, {
            changeReason: `Unsubscribed from "${masterRuleBook.name}"`,
            ...options,
          }),
        ),
      ),
  );
}

function* onCopyRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const rule = getRuleById(ruleId)(state);

  const lastOrder = getLastOrderByRuleBookId(rule.rule_book_id)(state);

  yield put(
    RuleAction.create(
      { ...rule, id: v4(), name: `${rule.name} (Copy)`, order: lastOrder + 1 },
      { changeReason: `Copied from "${rule.name}"`, ...options },
    ),
  );
}

function* onCopyMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRule = getMasterRuleById(masterRuleId)(state);

  const lastOrder = getLastOrderByMasterRuleBookId(masterRule.rule_book_id)(
    state,
  );

  yield put(
    MasterRuleAction.create(
      {
        ...masterRule,
        id: v4(),
        name: `${masterRule.name} (Copy)`,
        order: lastOrder + 1,
      },
      { changeReason: `Copied from "${masterRule.name}"`, ...options },
    ),
  );
}

function decrementRuleOrMasterRuleOrder(thisRule, prevRule) {
  return [
    { ...thisRule, order: prevRule.order },
    {
      ...prevRule,
      order: prevRule.order + 1,
    },
  ];
}

function incrementRuleOrMasterRuleOrder(thisRule, nextRule) {
  return [
    { ...thisRule, order: nextRule.order },
    {
      ...nextRule,
      order: nextRule.order - 1,
    },
  ];
}

function* onMoveUpRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const prevRule = rules[thisIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(thisRule, prevRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveDownRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const nextRule = rules[thisIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(thisRule, nextRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveUpMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const prevMasterRule = masterRules[currentIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(currentMasterRule, prevMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

function* onMoveDownMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const nextMasterRule = masterRules[currentIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(currentMasterRule, nextMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

/**
 * Generates a mapping of original item IDs to new UUIDs.
 *
 * @param {Array} items - The array of items, each with an `id` property.
 * @returns {Object} An object mapping each item's original `id` to a new UUID.
 */
function createIdReplacementMap(items) {
  return items.reduce((acc, item) => {
    acc[item.id] = v4();
    return acc;
  }, {});
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rule that already exist in the source Rule Book
 */
function copyRuleSets(ruleBookRuleSets, rulesInRuleSets, ruleIdReplacementMap) {
  const ruleSetById = keyBy(ruleBookRuleSets, "id");
  const rulesByRuleSetId = groupBy(rulesInRuleSets, "rule_set_id");
  const ruleSetsCopy = [];

  Object.values(ruleSetById).forEach(ruleSet => {
    const rules = rulesByRuleSetId[ruleSet.id] || [];
    if (!rules.length) {
      return;
    }
    ruleSetsCopy.push({
      ...ruleSet,
      id: undefined,
      // Give it a distinct name if it doesn't yet have a name
      name: ruleSet.name ? ruleSet.name : `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        id: ruleIdReplacementMap[rule.id],
      })),
    });
  });

  return ruleSetsCopy;
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rules that do not exist in a Rule Set
 */
function copyRulesWithoutRuleSets(rulesWithoutRuleSets, newIds) {
  const ruleSetsCopy = [];
  const rulesByInputField = groupBy(rulesWithoutRuleSets, "input_field");
  Object.values(rulesByInputField).forEach(rules => {
    ruleSetsCopy.push({
      input_field: rules[0].input_field,
      name: `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        input_field: null,
        id: newIds[rule.id],
      })),
    });
  });
  return ruleSetsCopy;
}

/**
 * Repalaces The prerequisite rule IDs in the copied RuleSets as part of a Copy Rule Book operation
 * @param ruleSetsCopy
 * @param idReplacementMap
 */
function rewritePrerequisiteIds(ruleSetsCopy, idReplacementMap) {
  Object.values(ruleSetsCopy).forEach(
    (
      ruleSet: RuleSet & {
        rules: Array<Rule & { copied_prerequisite_rule_ids: string[] }>;
      },
    ) => {
      ruleSet.rules.forEach(rule => {
        rule.rule_set_id = undefined;
        rule.copied_prerequisite_rule_ids = rule.prerequisite_rule_ids.map(
          ruleId => idReplacementMap[ruleId],
        );
        rule.prerequisite_rule_ids = undefined;
      });
    },
  );
}

function copyRuleGeneratorFactory(
  sourceRuleBookIdKey,
  destinationRuleBookIdKey,
  ruleBookGetterSelector,
  ruleBookRulesGetterSelector,
  ruleSetGetterSelector,
  ruleBookOfflineActionCreator,
) {
  return function* onCopyRuleBookGenFn(action) {
    const {
      [sourceRuleBookIdKey]: sourceRuleBookId,
      [destinationRuleBookIdKey]: destinationRuleBookId = v4(),
      options = null,
    } = action;
    const state = yield select();
    const ruleBook = ruleBookGetterSelector(sourceRuleBookId)(state);

    const ruleBookRules =
      ruleBookRulesGetterSelector(sourceRuleBookId)(state) || [];

    const ruleBookRuleSets =
      ruleSetGetterSelector(sourceRuleBookId)(state) || [];

    // To maintain the link between pre-requisite rules, create a lookup.
    const ruleIdReplacementMap = createIdReplacementMap(ruleBookRules);
    const ruleSetIdReplacementMap = createIdReplacementMap(ruleBookRuleSets);

    const [rulesInRuleSets, rulesWithoutRuleSets] = partition(
      ruleBookRules,
      rule => Boolean(rule.rule_set_id),
    );

    const ruleSetsCopy = [
      // For rules that are in rule sets, we need to copy the rule set first
      ...copyRuleSets(
        ruleBookRuleSets,
        rulesInRuleSets,
        ruleSetIdReplacementMap,
      ),
      // Add the rules that are not in and existing rule set to a new RuleSet, grouped with other Rules sharing the same input field
      ...copyRulesWithoutRuleSets(rulesWithoutRuleSets, ruleIdReplacementMap),
    ];

    // Wire up the Rule Ids from the Prerequisite Rules to the new Rule Ids
    rewritePrerequisiteIds(ruleSetsCopy, ruleIdReplacementMap);

    yield put(
      ruleBookOfflineActionCreator.create(
        {
          ...ruleBook,
          id: destinationRuleBookId,
          name: `${ruleBook.name} (Copy)`,
          rule_sets: ruleSetsCopy,
        },
        { changeReason: `Copied from "${ruleBook.name}"`, ...options },
      ),
    );
  };
}

const onCopyMasterRuleBook = copyRuleGeneratorFactory(
  "sourceMasterRuleBookId",
  "destinationMasterRuleBookId",
  getMasterRuleBookById,
  getMasterRulesByMasterRuleBookId,
  getMasterRuleSetsByMasterRuleBookId,
  MasterRuleBookAction,
);
const onCopyRuleBook = copyRuleGeneratorFactory(
  "sourceRuleBookId",
  "destinationRuleBookId",
  getRuleBookById,
  getRulesByRuleBookId,
  getRuleSetsByRuleBookId,
  RuleBookAction,
);

function* onUpdateManagedRuleBook(action) {
  const { sourceMasterRuleBookId, managedRuleBookId, ruleId } = action;
  const state = yield select();
  const masterRuleIds = getMasterRuleIdsByMasterRuleBookId(
    sourceMasterRuleBookId,
  )(state);
  const ruleIdsByMasterRuleId = selectRuleIdsByMasterRuleIdLookup(state);

  const rules = getRules(state);
  const masterRules = getMasterRules(state);
  const masterRuleSets = getMasterRuleSets(state);

  for (const masterRuleId of masterRuleIds) {
    const masterRule = masterRules[masterRuleId];
    const masterRuleSetId = masterRule.rule_set_id;
    const masterRuleSet = masterRuleSets[masterRuleSetId];

    let handledRuleSetId = null;

    const managedRuleSetIds = intersection(
      getRuleSetIdsByMasterRuleSetId(masterRuleSetId)(state),
      getRuleSetIdsByRuleBookId(managedRuleBookId)(state),
    );

    const managedRuleSetId =
      managedRuleSetIds.length > 0 ? managedRuleSetIds[0] : null;

    // If the master rule has a rule set and a managed rule set doesn’t exist - make one
    // Otherwise update the existing rule set to match the master rule set.
    if (masterRuleSetId) {
      if (!managedRuleSetId) {
        handledRuleSetId = v4();
        yield put(
          RuleSetAction.create(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRuleSetId,
              rule_book_id: managedRuleBookId,
            },
            { changeReason: `Rolled down from "${masterRule.name}"` },
          ),
        );
      } else {
        handledRuleSetId = managedRuleSetId;
        yield put(
          RuleSetAction.update(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRuleSetId,
              rule_book_id: managedRuleBookId,
            },
            { changeReason: `Rolled down from "${masterRule.name}"` },
          ),
        );
      }
    }
    // If there are no rules for the master rule yet, then we need to make one
    if (!ruleIdsByMasterRuleId[masterRuleId] && !ruleId) {
      yield put(
        RuleAction.create(
          {
            ...masterRule,
            id: v4(),
            comment: "",
            master_rule_id: masterRule.id,
            rule_book_id: managedRuleBookId,
            rule_set_id: handledRuleSetId,
          },
          { changeReason: `Rolled down from "${masterRule.name}"` },
        ),
      );
      // eslint-disable-next-line no-continue
      continue; // Skip the rest of this iteration
    }
    const filteredManagedRuleIds =
      ruleIdsByMasterRuleId[masterRuleId]?.filter(
        managedRuleId => ruleId && ruleId === managedRuleId,
      ) || [];
    // Otherwise we can check the content and is_deleted of the master rule and managed rule
    // to see if it needs updating
    for (const managedRuleId of filteredManagedRuleIds) {
      const managedRule = rules[managedRuleId];
      if (masterRule.is_deleted) {
        yield put(RuleAction.delete(managedRule.id));
      } else if (isManagedRuleOutOfSync(managedRule, masterRule)) {
        yield put(
          RuleAction.update(
            {
              content: masterRule.content,
              criteria: masterRule.criteria,
              id: managedRule.id,
              input_field: masterRule.input_field,
              quantity_output_format: masterRule.quantity_output_format,
              quantity_raw_format: masterRule.quantity_raw_format,
              unit_amount_output_format: masterRule.unit_amount_output_format,
              unit_amount_raw_format: masterRule.unit_amount_raw_format,
              rule_set_id: handledRuleSetId,
            },
            {
              changeReason: "Updated from master rulebook (via the frontend)",
            },
          ),
        );
      }
    }
  }
}

export default function* rootSaga() {
  yield takeEvery(
    RULE_BOOK.UNSUBSCRIBE_FROM_MASTER_RULE_BOOK.ACTION,
    onUnsubscribeFromMasterRuleBook,
  );
  yield takeEvery(
    RULE_BOOK.SUBSCRIBE_TO_MASTER_RULE_BOOK.ACTION,
    onSubscribeToMasterRuleBook,
  );
  yield takeEvery(
    MASTER_RULE_BOOK.UPDATE_MANAGED_RULE_BOOK.ACTION,
    onUpdateManagedRuleBook,
  );

  yield takeEvery(RULE.COPY.ACTION, onCopyRule);
  yield takeEvery(RULE.MOVE_UP.ACTION, onMoveUpRule);
  yield takeEvery(RULE.MOVE_DOWN.ACTION, onMoveDownRule);

  yield takeEvery(MASTER_RULE_BOOK.COPY.ACTION, onCopyMasterRuleBook);

  yield takeEvery(RULE_BOOK.COPY.ACTION, onCopyRuleBook);

  yield takeEvery(MASTER_RULE.COPY.ACTION, onCopyMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_UP.ACTION, onMoveUpMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_DOWN.ACTION, onMoveDownMasterRule);
}
